<template>
  <div class="step-question">
    <div class="question-header">
      <span class="q-num">Question {{ questionIndex + 1 }} of {{ totalQuestions }}</span>
    </div>
    <div class="question-text">{{ questionText }}</div>

    <!-- Base answer loading -->
    <div v-if="!answer || answer.length === 0" class="loading-answer">
      <div class="loader"></div>
      <span>Getting your personalized answer...</span>
    </div>

    <template v-else>
      <!-- Base answer card -->
      <div class="answer-card">
        <div class="answer-content" v-html="formatAnswer(answer)"></div>
      </div>

      <!-- Phase: Scoring base answer -->
      <feedback-form v-if="phase === 'scoring'" @submit="onBaseFeedback" />

      <!-- Phase: Choice after scoring -->
      <div v-if="phase === 'choice'" class="choice-area">
        <p class="choice-prompt">Did this answer fully give you what you were looking for?</p>
        <div class="choice-buttons">
          <button class="choice-btn followup-btn" @click="enterFollowUp">Ask a follow-up</button>
          <button class="choice-btn done-btn" @click="finishQuestion">I got what I needed</button>
        </div>
      </div>

      <!-- Follow-up conversation thread -->
      <div v-if="followUps.length > 0 && phase !== 'scoring'" class="followup-thread">
        <div v-for="(fu, i) in followUps" :key="i" class="followup-item">
          <div class="fu-question-bubble">
            <span class="fu-label">Follow-up {{ i + 1 }}</span>
            <span class="fu-text">{{ fu.question }}</span>
          </div>
          <div class="fu-answer-bubble" v-html="formatAnswer(fu.answer)"></div>
        </div>
      </div>

      <!-- Phase: Follow-up input -->
      <div v-if="phase === 'followup-input'" class="followup-input-area">
        <p class="fu-input-title">Ask a follow-up question</p>

        <div v-if="suggestedQuestions.length > 0" class="suggested-questions">
          <p class="suggested-label">Suggested questions:</p>
          <button v-for="(sq, i) in suggestedQuestions" :key="i" class="suggested-btn" @click="sendFollowUp(sq)">{{ sq }}</button>
        </div>

        <div class="custom-input">
          <textarea v-model="customQuestion" placeholder="Or write your own question..." rows="2" @keydown.enter.prevent="customQuestion.trim() && sendFollowUp(customQuestion.trim())"></textarea>
          <button class="send-btn" @click="sendFollowUp(customQuestion.trim())" :disabled="!customQuestion.trim()">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
          </button>
        </div>
      </div>

      <!-- Phase: Follow-up loading -->
      <div v-if="phase === 'followup-loading'" class="loading-answer">
        <div class="loader"></div>
        <span>Getting follow-up answer...</span>
      </div>

      <!-- Phase: After follow-up answer, choose next action -->
      <div v-if="phase === 'followup-choice'" class="choice-area">
        <div class="choice-buttons">
          <button v-if="followUps.length < 3" class="choice-btn followup-btn" @click="enterFollowUp">Ask another follow-up</button>
          <button class="choice-btn done-btn" @click="finishWithFollowUps">I got what I needed</button>
        </div>
      </div>

      <!-- Phase: Mini evaluation (forced after 3 follow-ups) -->
      <div v-if="phase === 'minieval'" class="minieval-area">
        <h3 class="minieval-title">Quick Follow-up Evaluation</h3>

        <div class="minieval-question">
          <label>Did the follow-ups help you get what you needed?</label>
          <div class="num-row">
            <button v-for="n in 11" :key="n-1" class="num-btn" :class="{ active: miniEval.additionalHelp === n-1 }" @click="miniEval.additionalHelp = n-1">{{ n-1 }}</button>
          </div>
          <div class="num-labels"><span>Not at all</span><span>Extremely</span></div>
        </div>

        <div class="minieval-question">
          <label>What was missing or unclear in the first answer?</label>
          <textarea v-model="miniEval.comment" placeholder="Optional..." rows="3"></textarea>
        </div>

        <button class="primary-btn" :disabled="miniEval.additionalHelp === null" @click="submitMiniEval">Continue</button>
      </div>
    </template>
  </div>
</template>

<script>
import { ref, reactive } from 'vue'
import { httpsCallable } from 'firebase/functions'
import { initFirebase } from '../../firebase'
import FeedbackForm from '../promptTest/FeedbackForm.vue'

const { functions } = initFirebase()

export default {
  name: 'StepBenchmarkWithFollowup',
  components: { FeedbackForm },
  props: {
    questionText: { type: String, required: true },
    questionIndex: { type: Number, required: true },
    totalQuestions: { type: Number, default: 8 },
    answer: { default: null }
  },
  emits: ['complete'],
  setup(props, { emit }) {
    const chatFn = httpsCallable(functions, 'chat')
    const getSuggestedFn = httpsCallable(functions, 'getSuggestedQuestions')

    const phase = ref('scoring')
    const baseFeedback = ref(null)
    const followUps = ref([])
    const suggestedQuestions = ref([])
    const customQuestion = ref('')
    const miniEval = reactive({ additionalHelp: null, comment: '' })

    const formatAnswer = (text) => {
      if (!text || typeof text !== 'string') return ''
      return text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').replace(/\n/g, '<br>')
    }

    const fetchSuggestions = async () => {
      try {
        const result = await getSuggestedFn({})
        const data = result.data
        if (Array.isArray(data)) {
          suggestedQuestions.value = data.slice(0, 3)
        } else if (data?.questions && Array.isArray(data.questions)) {
          suggestedQuestions.value = data.questions.slice(0, 3)
        } else if (data?.suggestedQuestions && Array.isArray(data.suggestedQuestions)) {
          suggestedQuestions.value = data.suggestedQuestions.slice(0, 3)
        }
      } catch (e) {
        console.log('[FollowUp] getSuggestedQuestions failed, using custom input only:', e.message)
        suggestedQuestions.value = []
      }
    }

    const onBaseFeedback = (feedback) => {
      baseFeedback.value = feedback
      phase.value = 'choice'
      scrollToBottom()
    }

    const finishQuestion = () => {
      emit('complete', {
        questionIndex: props.questionIndex,
        questionText: props.questionText,
        answer: props.answer,
        feedback: baseFeedback.value,
        choseFollowUp: false,
        followUps: [],
        miniEval: null
      })
    }

    const enterFollowUp = () => {
      phase.value = 'followup-input'
      customQuestion.value = ''
      fetchSuggestions()
      scrollToBottom()
    }

    const sendFollowUp = async (question) => {
      if (!question || !question.trim()) return
      const q = question.trim()
      customQuestion.value = ''
      phase.value = 'followup-loading'
      scrollToBottom()

      try {
        const result = await Promise.race([
          chatFn({ message: q }),
          new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 60000))
        ])
        let content = result.data?.response
        if (Array.isArray(content)) content = content.join('')

        followUps.value.push({ question: q, answer: content || 'No response received.' })

        // Extract suggestions from response for next round
        if (result.data?.suggestedQuestions && Array.isArray(result.data.suggestedQuestions)) {
          suggestedQuestions.value = result.data.suggestedQuestions.slice(0, 3)
        } else {
          suggestedQuestions.value = []
        }

        if (followUps.value.length >= 3) {
          phase.value = 'minieval'
        } else {
          phase.value = 'followup-choice'
        }
      } catch (e) {
        console.error('[FollowUp] Error:', e.message)
        followUps.value.push({ question: q, answer: 'Error: Could not get an answer.' })
        if (followUps.value.length >= 3) {
          phase.value = 'minieval'
        } else {
          phase.value = 'followup-choice'
        }
      }
      scrollToBottom()
    }

    const finishWithFollowUps = () => {
      // At least 1 follow-up done → show mini eval
      phase.value = 'minieval'
      scrollToBottom()
    }

    const submitMiniEval = () => {
      emit('complete', {
        questionIndex: props.questionIndex,
        questionText: props.questionText,
        answer: props.answer,
        feedback: baseFeedback.value,
        choseFollowUp: true,
        followUps: [...followUps.value],
        miniEval: {
          additionalHelp: miniEval.additionalHelp,
          comment: miniEval.comment
        }
      })
    }

    const scrollToBottom = () => {
      setTimeout(() => {
        const container = document.querySelector('.step-container')
        if (container) container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' })
      }, 100)
    }

    return {
      phase, baseFeedback, followUps, suggestedQuestions, customQuestion, miniEval,
      formatAnswer, onBaseFeedback, finishQuestion, enterFollowUp, sendFollowUp, finishWithFollowUps, submitMiniEval
    }
  }
}
</script>

<style scoped>
.step-question { max-width: 680px; margin: 0 auto; padding: 20px; text-align: center; }
.question-header { margin-bottom: 8px; }
.q-num { font-size: 12px; color: #a29bfe; text-transform: uppercase; letter-spacing: 1px; font-weight: 600; }
.question-text { font-size: 20px; font-weight: 700; color: #fff; margin-bottom: 24px; line-height: 1.4; }
.loading-answer { display: flex; align-items: center; gap: 12px; padding: 40px; justify-content: center; color: #9896a8; font-size: 14px; }
.loader { width: 24px; height: 24px; border: 3px solid rgba(108,99,255,0.15); border-radius: 50%; border-top-color: #a29bfe; animation: spin 0.8s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }
.answer-card { padding: 24px; background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.06); border-radius: 16px; margin-bottom: 4px; text-align: left; }
.answer-content { font-size: 15px; line-height: 1.75; color: rgba(255,255,255,0.8); }
.answer-content :deep(strong) { color: #a29bfe; }

/* Choice area */
.choice-area { padding: 28px 0; text-align: center; }
.choice-prompt { font-size: 16px; color: rgba(255,255,255,0.6); margin: 0 0 20px; }
.choice-buttons { display: flex; flex-direction: column; gap: 12px; max-width: 400px; margin: 0 auto; }
.choice-btn { padding: 16px 24px; border-radius: 12px; font-size: 16px; font-weight: 600; cursor: pointer; transition: all 0.2s; font-family: inherit; display: flex; align-items: center; justify-content: center; gap: 8px; }
.done-btn { background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.12); color: #fff; }
.done-btn:hover { background: rgba(255,255,255,0.08); }
.followup-btn { background: #6c5ce7; border: none; color: #fff; }
.followup-btn:hover { background: #7c6ff0; box-shadow: 0 6px 20px rgba(108,99,255,0.35); }
.followup-badge { font-size: 11px; background: rgba(255,255,255,0.15); padding: 2px 8px; border-radius: 8px; font-weight: 500; }

/* Follow-up thread */
.followup-thread { margin-top: 24px; display: flex; flex-direction: column; gap: 16px; padding-top: 24px; border-top: 1px solid rgba(108,99,255,0.15); }
.followup-item { display: flex; flex-direction: column; gap: 12px; }
.fu-question-bubble { display: flex; flex-direction: column; gap: 4px; padding: 14px 18px; background: rgba(108,99,255,0.08); border: 1px solid rgba(108,99,255,0.15); border-radius: 14px; text-align: left; }
.fu-label { font-size: 10px; font-weight: 700; color: #a29bfe; text-transform: uppercase; letter-spacing: 0.5px; }
.fu-text { font-size: 15px; color: #fff; font-weight: 500; }
.fu-answer-bubble { padding: 18px; background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.06); border-radius: 14px; text-align: left; font-size: 15px; line-height: 1.75; color: rgba(255,255,255,0.8); }
.fu-answer-bubble :deep(strong) { color: #a29bfe; }

/* Follow-up input */
.followup-input-area { margin-top: 24px; padding-top: 24px; border-top: 1px solid rgba(108,99,255,0.15); display: flex; flex-direction: column; gap: 16px; }
.fu-input-title { font-size: 16px; font-weight: 600; color: #fff; margin: 0; }
.fu-remaining { font-size: 13px; color: #a29bfe; font-weight: 500; }
.suggested-label { font-size: 12px; color: rgba(255,255,255,0.4); text-transform: uppercase; letter-spacing: 0.5px; margin: 0 0 8px; }
.suggested-questions { display: flex; flex-direction: column; gap: 8px; }
.suggested-btn { padding: 14px 18px; background: rgba(108,99,255,0.06); border: 1px solid rgba(108,99,255,0.15); border-radius: 12px; color: #fff; font-size: 14px; font-weight: 500; cursor: pointer; transition: all 0.2s; text-align: left; font-family: inherit; }
.suggested-btn:hover { background: rgba(108,99,255,0.12); border-color: #6c5ce7; }
.custom-input { display: flex; gap: 8px; align-items: stretch; }
.custom-input textarea { flex: 1; padding: 12px 16px; background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.15); border-radius: 12px; color: #fff; font-size: 14px; resize: none; font-family: inherit; min-height: 48px; box-sizing: border-box; }
.custom-input textarea::placeholder { color: rgba(255,255,255,0.3); }
.custom-input textarea:focus { outline: none; border-color: rgba(255,255,255,0.3); }
.send-btn { width: 48px; min-height: 48px; background: #6c5ce7; border: none; border-radius: 12px; color: #fff; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: all 0.2s; flex-shrink: 0; }
.send-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.send-btn:hover:not(:disabled) { background: #7c6ff0; }

/* Mini evaluation */
.minieval-area { margin-top: 28px; padding-top: 28px; border-top: 1px solid rgba(108,99,255,0.15); display: flex; flex-direction: column; gap: 32px; }
.minieval-title { font-size: 20px; font-weight: 700; color: #fff; margin: 0; text-align: center; }
.minieval-question { display: flex; flex-direction: column; gap: 12px; text-align: center; }
.minieval-question label { font-size: 15px; color: #fff; font-weight: 600; line-height: 1.4; }
.num-row { display: flex; gap: 6px; justify-content: center; }
.num-btn { width: 40px; height: 40px; border-radius: 10px; border: 1px solid rgba(255,255,255,0.1); background: rgba(255,255,255,0.04); color: rgba(255,255,255,0.5); font-size: 15px; font-weight: 600; cursor: pointer; transition: all 0.15s; font-family: inherit; }
.num-btn:hover { background: rgba(108,99,255,0.1); }
.num-btn.active { background: #6c5ce7; border-color: #6c5ce7; color: #fff; transform: scale(1.1); }
.num-labels { display: flex; justify-content: space-between; font-size: 11px; color: rgba(255,255,255,0.3); margin-top: 4px; width: 500px; max-width: 100%; margin-left: auto; margin-right: auto; }
.minieval-question textarea { padding: 14px 16px; background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.15); border-radius: 12px; color: #fff; font-size: 14px; resize: vertical; font-family: inherit; }
.minieval-question textarea::placeholder { color: rgba(255,255,255,0.3); }
.minieval-question textarea:focus { outline: none; border-color: rgba(255,255,255,0.3); }
.primary-btn { padding: 16px 24px; background: #6c5ce7; border: none; border-radius: 12px; color: #fff; font-size: 16px; font-weight: 600; cursor: pointer; transition: all 0.2s; width: 100%; }
.primary-btn:hover:not(:disabled) { background: #7c6ff0; box-shadow: 0 6px 20px rgba(108,99,255,0.35); }
.primary-btn:disabled { opacity: 0.4; cursor: not-allowed; }
</style>
